import * as React from "react";
import {
  AlertTriangle,
  Banknote,
  Briefcase,
  CalendarArrowDown,
  CalendarArrowUp,
  CircleDollarSign,
  FileText,
  Globe,
  Layers,
  Link2,
  User,
  Wallet
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ApprovalTimeline,
  TimelineNode
} from "@/components/requests/ApprovalTimeline";
import {
  BillsSummaryCard,
  DetailCard,
  DetailField,
  DocumentsSummaryCard,
  ReviewEditNote
} from "@/components/requests/DetailCard";
import { CurrencyConversionCard } from "@/components/requests/CurrencyConversionCard";
import { DownloadReportButton } from "@/components/requests/DownloadReportButton";
import { TripDetailFields } from "@/components/requests/TripRows";
import { fetchRequestLinkedAdvance, fetchTravelRequest } from "../../api";
import {
  Advance,
  CLAIM_METHOD_LABEL,
  OTHER_CLAIM_TYPE_LABEL,
  TRAVEL_REQUEST_STATUS_LABEL,
  TravelRequestSummary
} from "../../types";
import { formatAdvanceAmounts } from "@/lib/advance";
import { ScrollArea } from "../ui/scroll-area";
import { formatDateTime } from "@/lib/datetime";

// Build the Requestor → Approver → Finance approver timeline from the real
// request state. The detail API resolves only the requester's name (approver
// records carry just ids - see requests_api.py), so the two approver nodes are
// labelled by role, with the Finance node named generically ("Finance team").
//
// A Manager rejection ends the journey at the Approver node - there's no
// Finance stage to show as a third step, so that node is marked "skipped".
function buildRequestTimeline(detail: TravelRequestSummary): TimelineNode[] {
  const requesterName =
    detail.requester?.displayName || detail.requester?.email || "Requestor";
  // Approver names are now snapshotted onto the approval records at
  // submission (see db/approvals_store.py) - show the real person where we
  // have one, falling back to the generic role label for an older record or
  // an unassigned stage.
  const autoApproved = !!detail.approval?.autoApproved;
  const managerName = detail.approval?.approverName || "Reporting manager";
  // financeApproval doesn't exist until the Manager approves, so before then
  // fall back to the finance approver the requester picked at submission
  // (snapshotted on the request) rather than the generic label.
  const financeName =
    detail.financeApproval?.approverName ||
    detail.financeApproverNameAtSubmission ||
    "Finance team";
  const managerDecided =
    !!detail.approval?.decidedAt ||
    detail.approval?.status === "Approved" ||
    detail.approval?.status === "Rejected";
  const managerRejected = detail.rejectedBy === "Manager";
  const financeRejected = detail.rejectedBy === "Finance";
  const fullyApproved =
    detail.status === "Approved" || detail.status === "Completed";
  // A requester with no reporting manager (e.g. the Founder) has the Manager
  // stage skipped entirely - the request goes straight to Finance, so
  // `approval` is null even though it's moved past Submitted (see
  // requests_api.py's _route_for_manager_approval). Show that node as skipped
  // rather than a phantom "pending/approved" manager who never existed.
  const managerSkipped = !detail.approval && detail.status !== "Submitted";

  const nodes: TimelineNode[] = [
    {
      role: "Requestor",
      name: requesterName,
      state: "done",
      statusLabel: "Submitted"
    }
  ];

  // ResubmitRequested: the approval/financeApproval sub-document for
  // whichever stage sent it back is deliberately left untouched (still
  // Pending - see db/approvals_store.py's request_resubmit) so the
  // employee's eventual resubmission routes straight back to the same
  // approver with no new approval record. That means the normal
  // managerDecided/managerRejected/financeRejected checks below would
  // naturally read this as an ordinary pending decision - not wrong, but
  // not distinguishing "waiting on the employee to fix something" from
  // "waiting on the approver to decide." Show that explicitly instead.
  if (detail.status === "ResubmitRequested") {
    const sentBackByFinance = detail.resubmitRequestedBy === "Finance";
    nodes.push({
      role: "Approver",
      name: managerName,
      state: sentBackByFinance ? "approved" : "rejected",
      statusLabel: sentBackByFinance ? "Approved" : "Sent back for changes"
    });
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: sentBackByFinance ? "rejected" : "skipped",
      statusLabel: sentBackByFinance
        ? "Sent back for changes"
        : "Not reached yet"
    });
    return nodes;
  }

  // Approver (Manager) node.
  if (managerRejected) {
    nodes.push({
      role: "Approver",
      name: managerName,
      state: "rejected",
      statusLabel: "Rejected"
    });
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: "skipped",
      statusLabel: "Not reached"
    });
    return nodes;
  }
  if (autoApproved) {
    // Requester has no reporting manager - the Manager stage was stamped
    // automatically (see requests_api.py's _route_for_manager_approval).
    nodes.push({
      role: "Approver",
      name: "Auto-approved",
      state: "approved",
      statusLabel: "Auto-approved"
    });
  } else if (managerSkipped) {
    // Older record raised before auto-approval was stamped - its Manager
    // stage is simply absent (approval null). Show it as skipped.
    nodes.push({
      role: "Approver",
      name: "No manager",
      state: "skipped",
      statusLabel: "Skipped"
    });
  } else {
    const managerApproved =
      managerDecided ||
      detail.status === "PendingFinance" ||
      fullyApproved ||
      financeRejected;
    nodes.push({
      role: "Approver",
      name: managerName,
      state: managerApproved ? "approved" : "pending",
      statusLabel: managerApproved ? "Approved" : "Pending"
    });
  }

  // Finance approver node.
  if (financeRejected) {
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: "rejected",
      statusLabel: "Rejected"
    });
  } else if (fullyApproved) {
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: "approved",
      statusLabel: detail.status === "Completed" ? "Settled" : "Approved"
    });
  } else {
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: "pending",
      statusLabel: "Pending"
    });
  }
  return nodes;
}

// Read-only detail card an approver (Manager or Finance) opens before
// deciding - the My Approvals / Approver Queue tables only carry a thin
// Approval record (status/comment/ids), not the actual trip and bills, so
// there was previously no way to see what's actually being approved short
// of the requester describing it out of band. Fetched fresh on open rather
// than passed the caller's row data, since callers only have that thin
// record. Approve/Reject stay on the row exactly as before - this is
// view-only.
export function RequestDetailDialog({
  travelRequestId,
  open,
  onOpenChange,
  highlight = false,
  footerActions
}: {
  travelRequestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Plays a one-shot highlight flash on open - set true only when this
   *  dialog was opened by following a notification "View Request" link
   *  (see MyApprovals.tsx/MyRequests.tsx/ApproverQueue.tsx's own id-from-
   *  URL handling), as arrival confirmation among a list of many requests.
   *  Every other caller omits this and is unaffected. */
  highlight?: boolean;
  /** Rendered in the footer alongside Download report, pushed to the
   *  OPPOSITE end (Download report moves to the left when this is given,
   *  stays at its original right-aligned spot otherwise - see the
   *  SheetFooter className below). This dialog stays read-only/action-
   *  agnostic on its own - it's reused by MyRequests.tsx (the requester's
   *  own view, no actions), ApproverQueue.tsx (Finance's "Mark paid," a
   *  different action entirely) and MyApprovals.tsx (a Manager's Approve/
   *  Reject) - so the actions themselves are the CALLER's job, built from
   *  data the caller already has (e.g. MyApprovals.tsx already holds the
   *  full Approval row this travelRequestId came from, same object its own
   *  table-row action cell uses), not something fetched again in here.
   *  Omit for a dialog with nothing actionable at this point. */
  footerActions?: React.ReactNode;
}) {
  const [detail, setDetail] = React.useState<TravelRequestSummary | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Fetched separately, best-effort - the request only carries the linked
  // advance's id (see linkedAdvanceId below), not anything about it, so a
  // second call resolves the full advance for display. Left null (silently)
  // on failure/absence rather than blocking the rest of the dialog on it.
  // fetchRequestLinkedAdvance, NOT the generic fetchAdvance - this dialog is
  // opened by a Manager/Finance approver of the REQUEST, who can be a
  // different person than the advance's own approver, so fetchAdvance's
  // scoping (requester / the advance's own approver) silently 404ed for
  // them - the card always showed "-" even though the request genuinely
  // had an advance linked (see requests_api.py's
  // get_request_linked_advance for the full reasoning).
  const [linkedAdvance, setLinkedAdvance] = React.useState<Advance | null>(
    null
  );

  React.useEffect(() => {
    if (!open || !travelRequestId) {
      setDetail(null);
      setError(null);
      setLinkedAdvance(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchTravelRequest(travelRequestId)
      .then(setDetail)
      .catch(() => setError("Could not load this request."))
      .finally(() => setLoading(false));
  }, [open, travelRequestId]);

  React.useEffect(() => {
    if (!detail?.linkedAdvanceId || !travelRequestId) {
      setLinkedAdvance(null);
      return;
    }
    fetchRequestLinkedAdvance(travelRequestId)
      .then(setLinkedAdvance)
      .catch(() => setLinkedAdvance(null));
  }, [detail?.linkedAdvanceId, travelRequestId]);

  // The ledger below (CurrencyConversionCard) always shows the advance
  // amount and net payable/owed, broken out per currency, whenever there's a
  // linked advance - showing this simpler card too would just repeat the
  // same figures, so it's never shown alongside the ledger.
  //
  // Widen the sheet whenever the ledger itself renders at all (any bill or
  // advance - see CurrencyConversionCard's own hasAnyCurrency), not just
  // when 2+ currencies are involved: its table is always the same
  // min-w-[57.5rem] regardless of row count, and a single-currency row whose
  // settlement currency was changed to something else still needs the full
  // width - gating on spansMultipleCurrencies here left THAT case stuck at
  // the narrow default width with a horizontal scrollbar, confirmed live.
  const ledgerRenders = !!detail && (detail.expenses.length > 0 || !!detail.linkedAdvanceId);
  // An Other reimbursement (City travel / Miscellaneous) has no trip - its
  // own fields below instead of the trip/allowance ones.
  const otherType = detail?.requestKind === "other" ? detail.claimType ?? null : null;
  const isOther = detail?.requestKind === "other";

  const rejectionComment =
    detail && detail.status === "Rejected"
      ? detail.rejectedBy === "Finance"
        ? detail.financeApproval?.comment
        : detail.approval?.comment
      : null;
  const approverComment =
    detail && detail.status !== "Rejected"
      ? detail.financeApproval?.comment || detail.approval?.comment
      : null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        // key: re-mounts (and so re-triggers the one-shot animation) each
        // time a DIFFERENT highlighted request opens - without it, the
        // className alone would only ever animate on this component's
        // first mount, not on every subsequent open (open/travelRequestId
        // change while this dialog itself stays mounted the whole time the
        // page is open).
        key={highlight ? travelRequestId : "no-highlight"}
        className={cn(
          "flex w-full flex-col gap-0 sm:max-w-xl",
          // Room for the reconciliation table's min-w-[57.5rem] plus sheet and
          // card padding, so laptop/desktop shows it without a horizontal
          // scroll (lg, not xl: a scaled laptop screen is often < 1280px).
          ledgerRenders && "lg:max-w-[65rem]",
          highlight && "animate-highlight-flash"
        )}
      >
        <SheetHeader>
          <SheetTitle>Request details</SheetTitle>
          <SheetDescription>
            {isOther
              ? "Everything submitted with this reimbursement request."
              : "Everything submitted with this travel request."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mx-6 mt-4 min-w-0 flex-1">
          {/* grid + minmax(0,1fr), not block/flex: Radix's ScrollArea wraps
              this in an internal `display: table` div sized to ITS content's
              max-content width (see @radix-ui/react-scroll-area's
              ScrollAreaContent - needed so it can detect real scroll-size
              changes), so a wide descendant (the reconciliation ledger's
              table, deliberately min-w-[57.5rem] - see
              CurrencyConversionCard.tsx) was inflating that wrapper and, with
              it, the whole sheet, rather than being clipped and scrolled
              in place by its own overflow-x-auto. A grid track is the one
              layout mode that clamps a child to the available space
              regardless of the child's own intrinsic width - block/flex
              children instead negotiate their width WITH an auto-sizing
              ancestor like Radix's table-display wrapper, which is exactly
              the circular sizing that let the table win. */}
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] px-6">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            {!loading && detail && (
              <div className="space-y-4 text-sm">
                <ApprovalTimeline nodes={buildRequestTimeline(detail)} />

                <DetailCard icon={FileText} title="Request details">
                  {/* The sheet widens to lg:max-w-[65rem] whenever the
                      ledger renders (see ledgerRenders above) - an extra
                      column there fills that width instead of leaving the
                      2-column grid's fields stranded with empty space
                      beside them. */}
                  <dl
                    className={cn(
                      "grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2",
                      ledgerRenders && "lg:grid-cols-3"
                    )}
                  >
                    {detail.requester &&
                      (detail.requester.displayName ||
                        detail.requester.email) && (
                        <DetailField
                          icon={User}
                          label="Requested by"
                          className={cn("col-span-1 sm:col-span-2", ledgerRenders && "lg:col-span-3")}
                          value={
                            <span>
                              {detail.requester.displayName || "-"}
                              {detail.requester.email && (
                                <span className="ml-1.5 font-normal text-muted-foreground">
                                  {detail.requester.email}
                                </span>
                              )}
                            </span>
                          }
                        />
                      )}
                    <DetailField
                      icon={Briefcase}
                      label="Designation"
                      value={detail.designationAtSubmission || "-"}
                    />
                    {isOther ? (
                      <>
                        <DetailField
                          icon={Wallet}
                          label="Claim type"
                          value={otherType ? OTHER_CLAIM_TYPE_LABEL[otherType] : "-"}
                        />
                        <DetailField icon={Globe} label="Country" value={detail.country || "-"} />
                        <DetailField icon={CircleDollarSign} label="Currency" value={detail.currency || "-"} />
                      </>
                    ) : (
                      <>
                        <TripDetailFields trip={detail} />
                        <DetailField
                          icon={CalendarArrowUp}
                          label="Departure"
                          value={formatDateTime(detail.departureAt)}
                        />
                        <DetailField
                          icon={CalendarArrowDown}
                          label="Arrival"
                          value={formatDateTime(detail.arrivalAt)}
                        />
                        <DetailField
                          icon={Wallet}
                          label="Claim method"
                          value={CLAIM_METHOD_LABEL[detail.claimMethod]}
                        />
                        <DetailField
                          icon={Layers}
                          label="Full / half days"
                          value={`${detail.fullDayCount} / ${detail.halfDayCount}`}
                        />
                        <DetailField
                          icon={CircleDollarSign}
                          label="Allowance amount"
                          value={`${detail.currency} ${detail.estimatedAllowanceAmount.toLocaleString()}`}
                        />
                      </>
                    )}
                    {detail.status === "Completed" &&
                      (detail.settledAmounts?.length ? (
                        <DetailField
                          icon={Banknote}
                          label="Amount paid"
                          value={formatAdvanceAmounts(detail.settledAmounts)}
                        />
                      ) : (
                        // Settled before Finance could pay in several
                        // currencies - one gross figure in the request's own.
                        detail.settledAmount != null && (
                          <DetailField
                            icon={Banknote}
                            label="Settled amount"
                            value={`${detail.currency} ${detail.settledAmount.toLocaleString()}`}
                          />
                        )
                      ))}
                    <DetailField
                      icon={Link2}
                      label="Advance linked"
                      value={
                        <Badge variant={detail.linkedAdvanceId ? "default" : "outline"}>
                          {detail.linkedAdvanceId ? "Yes" : "No"}
                        </Badge>
                      }
                    />
                    <DetailField
                      icon={Banknote}
                      label="Status"
                      value={
                        <StatusBadge
                          status={detail.status}
                          label={TRAVEL_REQUEST_STATUS_LABEL[detail.status]}
                        />
                      }
                    />
                  </dl>
                </DetailCard>

                {/* The comment is optional on either stage's decision (see
                Approval.comment in types.ts) - shown whichever stage
                actually left one. On a Rejected request this is the
                rejection reason; rejectedBy says which stage rejected, so
                that stage's own comment is the relevant one, not whichever
                happens to be non-null (a Manager-rejected request has no
                financeApproval at all, but an old document could in
                principle carry a stale comment on the other stage). */}
                {rejectionComment && (
                  <DetailCard
                    icon={AlertTriangle}
                    title="Rejection details"
                    tone="destructive"
                  >
                    <DetailField
                      icon={FileText}
                      label="Rejection reason"
                      value={
                        <span className="whitespace-pre-wrap">
                          {rejectionComment}
                        </span>
                      }
                    />
                  </DetailCard>
                )}

                {approverComment && (
                  <DetailCard icon={FileText} title="Approver comment">
                    <p className="whitespace-pre-wrap">{approverComment}</p>
                  </DetailCard>
                )}

                {detail.status === "ResubmitRequested" &&
                  detail.resubmitComment && (
                    <DetailCard
                      icon={AlertTriangle}
                      title={`Changes requested by ${detail.resubmitRequestedBy === "Finance" ? "Finance" : "the reporting manager"}`}
                      tone="destructive"
                    >
                      <p className="whitespace-pre-wrap">
                        {detail.resubmitComment}
                      </p>
                    </DetailCard>
                  )}

                {detail.reviewEdit && (
                  <ReviewEditNote reviewEdit={detail.reviewEdit} />
                )}

                {detail.flightDetails && (
                  <DetailCard icon={FileText} title={isOther ? "Purpose" : "Purpose of travel"}>
                    <p className="whitespace-pre-wrap">
                      {detail.flightDetails}
                    </p>
                  </DetailCard>
                )}

                {/* The reconciliation ledger below always renders whenever
                    there's a linked advance, and already shows the advance
                    amount and net payable/owed per currency - this surface
                    never showed a plainer "Linked advance" summary alongside
                    it. */}

                {/* Reimbursement always shows its bills (they ARE the
                    claim). Fixed Allowance shows them only when the employee
                    recorded some, as tracking-only (they never change the
                    allowance). */}
                {(detail.claimMethod === "reimbursement" ||
                  detail.expenses.length > 0) && (
                  <BillsSummaryCard
                    requestId={detail.id}
                    expenses={detail.expenses}
                    currency={detail.currency}
                    estimatedAllowanceAmount={detail.estimatedAllowanceAmount}
                    limitsByCurrency={detail.policyLimitsByCurrencyAtSubmission}
                    trackingOnly={detail.claimMethod === "fixed_allowance"}
                    noLimit={isOther}
                    billFields={otherType ?? "travel"}
                  />
                )}

                {/* Renders nothing when there's at most one currency across
                    the bills and linked advance combined - see
                    CurrencyConversionCard's own doc comment. readOnly: this
                    is a plain "view this request" sheet, not the
                    approve/settle action itself - editing a settlement
                    currency or setting an FX override belongs in Review &
                    Approve / Mark settled, not here. */}
                <CurrencyConversionCard
                  travelRequestId={detail.id}
                  expenses={detail.expenses}
                  advanceAmounts={linkedAdvance?.amounts}
                  fxOverrides={detail.fxOverrides}
                  settlementTargetByCurrency={detail.settlementTargetByCurrency}
                  defaultTargetCurrency={detail.currency}
                  // An Other reimbursement has no departure - its advance
                  // converts at the request's created date.
                  departureDate={detail.departureAt || detail.createdAt}
                  claimMethod={detail.claimMethod}
                  // Fixed Allowance only - without this the card's ledger
                  // never folds the allowance into the defaultTargetCurrency
                  // row (see CurrencyConversionCard's own ledgerExpenses doc
                  // comment), so the "Net payable"/"Owed by" tile silently
                  // fell back to the OLD bills-only calculation here
                  // (confirmed live: a Fixed Allowance + Domestic request
                  // showed "Owed by the employee" computed from bills vs.
                  // advance alone, with the allowance never added - this
                  // prop was missing entirely from this call).
                  estimatedAllowanceAmount={
                    detail.claimMethod === "fixed_allowance" ? detail.estimatedAllowanceAmount : undefined
                  }
                  readOnly
                />

                {/* Plain supporting attachments - shown only when some were
                    added (no empty-state card, unlike bills). */}
                {(detail.documents?.length ?? 0) > 0 && (
                  <DocumentsSummaryCard
                    requestId={detail.id}
                    documents={detail.documents!}
                  />
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {!loading && detail && detail.status !== "Draft" && (
          <SheetFooter
            className={cn(
              "mt-4 gap-2 sm:gap-2",
              // Download report moves to the left end, ROW layout at every
              // width (not just sm: and up), once there's something else in
              // the footer to balance it against. SheetFooter's own default
              // (CLI-owned, not hand-edited here) is flex-col-reverse on
              // phones, sm:flex-row/sm:justify-end from tablet up - correct
              // for every OTHER caller with just one button, but with two
              // side by side is what was actually wanted here, phone
              // included, not just desktop.
              footerActions && "flex-row justify-between"
            )}
          >
            <DownloadReportButton travelRequestId={detail.id} variant="full" />
            {footerActions}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
